import React from 'react';
import { Howl } from 'howler';
import { SegmentService } from 'analytics/segment-generated';
import { SliceContext } from 'components/Slices/types';
import { ABPlayersHowls, Versions, ABPlayersSources, Item } from '../types';
import { ABPlayersProgress } from './ABPlayersProgress';

export type ABPlayersProps = {
    sources: ABPlayersSources[];
    version: Versions;
    current: number;
    isPlaying: boolean;
    currentStyle: Item[];
    segmentService: SegmentService;
    sliceContext: SliceContext;
};

export type ABPlayersState = {
    players: ABPlayersHowls[];
};

const unselecteVersion = (selectedVersion: Versions) =>
    selectedVersion === Versions.mastered ? Versions.original : Versions.mastered;

const setMutes = (players: ABPlayersHowls[]) => (props: ABPlayersProps) => {
    players[props.current][unselecteVersion(props.version)].mute(true);
    players[props.current][props.version].mute(false);
};

const play = (players: ABPlayersHowls[]) => (props: ABPlayersProps) => {
    setMutes(players)(props);
    Object.values(players[props.current]).map(p => p.play());

    const currentStyle = props.currentStyle[props.current].mastering_demos_style.raw;
    props.segmentService.trackAudioPlayerLaunched(
        {
            'L - Destination Link': props.sources[props.current].original,
            'L - Asset Genre': currentStyle ? currentStyle[0].text : '',
        },
        props.sliceContext,
    );
};

const pause = (players: ABPlayersHowls[]) =>
    players.map(song => {
        song[Versions.mastered].pause();
        song[Versions.original].pause();
    });

const stopAll = (players: ABPlayersHowls[]) =>
    players.map(song => {
        song[Versions.mastered].stop();
        song[Versions.original].stop();
    });

const preloadCurrent = (players: ABPlayersHowls[]) => (props: ABPlayersProps) =>
    Object.values(players[props.current]).map(p => p.load());

const createPlayers = (sources: ABPlayersSources[]) =>
    sources.map(s => {
        return {
            [Versions.original]: new Howl({
                src: s[Versions.original],
                preload: false,
                loop: true,
            }),
            [Versions.mastered]: new Howl({
                src: s[Versions.mastered],
                preload: false,
                loop: true,
            }),
        };
    });

class ABPlayers extends React.PureComponent<ABPlayersProps, ABPlayersState> {
    // TODO: Refactor to remove useless state
    state = {
        players: createPlayers(this.props.sources),
    };
    songProgress: ABPlayersProgress;

    componentDidMount() {
        preloadCurrent(this.state.players)(this.props);

        this.songProgress = new ABPlayersProgress(this.state.players[0]);
    }

    componentWillUnmount() {
        stopAll(this.state.players);
        this.songProgress.destroy();
    }

    componentDidUpdate(prevProps: ABPlayersProps) {
        if (this.props.isPlaying !== prevProps.isPlaying) {
            if (this.props.isPlaying) {
                play(this.state.players)(this.props);
                this.songProgress.startProgress();
            } else {
                pause(this.state.players);
            }
        }

        if (this.props.version !== prevProps.version) {
            setMutes(this.state.players)(this.props);
        }

        if (this.props.current !== prevProps.current) {
            preloadCurrent(this.state.players)(this.props);
            stopAll(this.state.players);
            this.songProgress.clearProgress();
            this.songProgress.updateSource(this.state.players[this.props.current]);
        }
    }

    render() {
        return null;
    }
}

export { ABPlayers };
